<script>
  import debounce from "lodash/debounce";
  import { createEventDispatcher, tick } from "svelte";
  import pasteAsPlaintext from "#src/extensions/dom/paste-as-plaintext.js";

  export let border = false;
  /** @type {string | null} */
  export let label = "Label";
  /** @type {string | null} */
  export let labelWidth = null;
  export let value;
  export let disabled = false;
  export let readonly = false;
  export let placeholder = "";
  export let outerBorder = true;
  export let bg = true;

  let input;

  const dispatch = createEventDispatcher();

  let localValue = value || "";
  let focused = false;

  $: lw = labelWidth ? `width:${labelWidth};` : "";
  $: refresh(value, input);

  function refresh(value, input) {
    if (input && value !== input.innerText.trim()) localValue = value || "";
  }

  function update(e) {
    if (input) {
      const v = input.innerText.trim();
      if (v !== value) {
        value = v;
        dispatch("input", { value: v });
      }
    }
  }

  async function focus() {
    await tick();
    if (input) {
      input.focus();
    }
  }

  function focusInput() {
    focused = true;
  }

  const debouncedUpdate = debounce(update, 1000);

  function handleKeydown(e) {
    if (e.key === "Escape") {
      input.blur();
    }
  }

  function blur(e) {
    update();
    focused = false;
    if (placeholder && !value) {
      e.target.innerHTML = "";
    }
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="prop-container"
  class:bg-white={bg}
  class:has-outer-border={outerBorder}
  class:has-inner-border={!outerBorder}
  class:focused
  class:readonly={readonly || disabled}
  class:visible-border={border}
  on:click|stopPropagation={focus}>
  {#if label != null}
    <div class="label" style={lw}>
      <slot name="label">
        {label}
      </slot>
    </div>
  {/if}
  {#if disabled || readonly}
    <div bind:this={input} class="editable-div whitespace-pre-wrap">
      {localValue}
    </div>
  {:else}
    <div
      bind:this={input}
      contenteditable
      class="editable-div whitespace-pre-wrap"
      data-placeholder={placeholder}
      bind:innerHTML={localValue}
      on:focus={focusInput}
      on:blur={blur}
      on:paste={pasteAsPlaintext}
      on:keydown={handleKeydown}
      on:input={debouncedUpdate} />
  {/if}
</div>

<style lang="scss">
  .editable-div {
    border: none;
    background-color: none;
    outline: none;
    min-width: 0;
    max-width: 100%;
  }

  .prop-container {
    @apply w-full rounded;

    &::after {
      content: "";
      display: table;
      clear: both;
    }

    &.has-outer-border {
      @apply p-2;

      &:hover:not(.readonly):not(.focused),
      &.visible-border {
        @apply ring-1 ring-inset ring-gray-300;
      }
      &.focused {
        @apply ring-2 ring-inset ring-blue-500;
      }
    }

    .label {
      @apply truncate text-gray-400;
      float: left;
      margin-right: 0.5rem;
    }

    .editable-div {
      @apply min-w-0 bg-transparent;
    }

    &.has-inner-border {
      .editable-div {
        @apply p-1 rounded;
      }

      .label {
        @apply pt-1;
      }
    }

    &.has-inner-border:not(.readonly):not(.focused) {
      .editable-div:hover {
        @apply ring-1 ring-inset ring-gray-300;
      }
    }

    &.has-inner-border.focused {
      .editable-div {
        @apply ring-2 ring-inset ring-blue-500;
      }
    }

    .editable-div:empty:before {
      content: attr(data-placeholder);
      color: #a0aec0;
    }
  }
</style>
